import KpiCard, { KpiRow } from "@/components/KpiCard";
import InfoTooltip from "@/components/InfoTooltip";
import { fmt } from "@/lib/format";
import Panel from "../Panel";

const MODEL_RATES = [
  { tier: "인피니티", model: "Claude Fable 5", usd: "$10 / $50", inNc: 60, outNc: 300 },
  { tier: "울트라 (기본)", model: "Claude Opus 4.8", usd: "$5 / $25", inNc: 30, outNc: 150 },
  { tier: "프로", model: "Claude Sonnet 5", usd: "$3 / $15", inNc: 18, outNc: 90 },
  { tier: "자유 대화 전용", model: "GPT-5.5", usd: "$5 / $30", inNc: 30, outNc: 180 },
];

const CHECKLIST = [
  "AWS 청구서(Bedrock) ↔ 원장 산출 원가 대조 — 오차 ±5% 이내 확인",
  "OpenAI Costs API ↔ 원장 대조 — 커버리지 90% 이상 유지",
  "환율 상수(1,500) vs 실환율 — 1,550 돌파 시 상수 조정",
  "단가표(docs/reference/claude-bedrock-pricing.md + 이 탭 요율표) 공식 페이지 재검증",
  "이미지 정액 vs 스펙 포함 실원가 — 도구별 배수표에서 3배 유지 확인",
  "월간 비교 시 일수 보정(30일/31일 ≈ 3.3% 왜곡) 적용",
  "Exa(웹서치·URL 인제스트) 대시보드 실지출 ↔ 원장 exa row 대조 — 단가 상수 재검증",
];

const POLICY_HISTORY = [
  { date: "2026-05-22", what: "버퍼 1.042 → 2.0", why: "원가 회수 → 마진 확보 전환" },
  { date: "2026-06-02", what: "버퍼 2.0 → 4.0 + 충전 ×2 프로모 개시", why: "DMA 1위 기념 100일 (실효 2.0 유지)" },
  { date: "2026-07-19", what: "이미지 스펙 호출 원가 기록 개시", why: "미계측 82% 발견 → imagegen_spec 관측" },
  { date: "2026-09-09", what: "충전 ×2 프로모 종료 예정", why: "실효 배수 2.0 → 4.0 복귀", future: true },
];
const THRESHOLDS = [
  { name: "마진 배수", limit: "< 3.5", meaning: "미계측 비용·단가 개정·버그 신호 (설계값 4.0)" },
  { name: "일 지출", limit: "직전 7일 평균 × 2", meaning: "폭주·어뷰징·버그" },
  { name: "단일 사용자 기간 원가", limit: "> ₩30,000", meaning: "어뷰징 확인 또는 B2B 전환 후보" },
  { name: "OpenAI 커버리지", limit: "< 90%", meaning: "미계측 호출 경로 존재" },
];
const CREDIT_POLICY = [
  { k: "1 크레딧", v: "= 1원", note: "명목 가치 — 프로모 중 실구매가는 0.5원" },
  { k: "가입 보너스", v: "300 크레딧", note: "자유 대화 수 회 분량 — 체험 설계상 상향 검토 중" },
  { k: "최소 잔액 가드", v: "100 크레딧", note: "미만이면 채팅 차단(INSUFFICIENT_CREDIT) → 충전 유도" },
  { k: "충전 프로모", v: "결제액 × 2 지급", note: "2026-06-02 ~ 09-09 · 실효 마진 = 명목 ÷ 2" },
];
const IMAGE_FLAT_ROWS = [
  { q: "low", cost: 12, sell: 48, note: "$0.008 × 1,500 × 4.0" },
  { q: "medium", cost: 80, sell: 318, note: "$0.053 × 1,500 × 4.0 — 운영 상한(기본)" },
  { q: "high", cost: 255, sell: 1020, note: "$0.170 × 1,500 × 4.0 — 속도·비용상 잠금" },
];

function RulesSegment({ d }) {
  return (
    <>
      {/* ① 핵심 정책 — 지금 무엇이 적용 중인가 */}
      <KpiRow cols={4}>
        <KpiCard label="마진 배수 (버퍼)" value="4.0×" tone="brand" hero
                 sublabel="차감 = 원가 × 4.0 — 모든 종량 도구 공통" />
        <KpiCard label="환율 상수" value={`${fmt.int(d.fxRate || 1500)}원/$`}
                 sublabel="분기 점검 · 실환율 1,550 돌파 시 조정" />
        <KpiCard label="충전 프로모" value={d.promoX2 ? "×2" : "종료"} tone="good"
                 sublabel={d.promoX2 ? "9/9까지 · 실효 배수 = 명목 ÷ 2" : "실효 = 명목"} />
        <KpiCard label="리전 할증 (CRIS)" value="×1.1"
                 sublabel="us.* 추론 프로파일 경유분 — 차감·원가 양쪽 반영" />
      </KpiRow>

      {/* ② 공식 + 요율표 — 원가와 판매가를 나란히: 마진 구조가 표에서 보인다 */}
      <Panel title="차감 공식 · 모델별 요율표"
             action={<span className="text-[12px] text-ink-400">소스: credit_hook.py — 개정 시 이 표도 갱신(체크리스트 4번)</span>}>
        <div className="rounded-xl bg-ink-50/70 px-3.5 py-3 font-mono text-[12px] leading-relaxed text-ink-700">
          차감(크레딧) = [ 입력×입력요율 + 출력×출력요율 + 캐시읽기×입력요율×<b>0.10</b> + 캐시쓰기×입력요율×<b>1.25</b> ] ÷ 1,000 × CRIS 1.1<br />
          요율(크레딧/1k토큰) = 공시단가($/1M) × 환율 1,500 × 마진 배수 4.0 ÷ 1,000
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">티어 (앱 표기)</th>
                <th className="pb-2 font-medium">모델</th>
                <th className="pb-2 text-right font-medium">공시가 ($/1M)</th>
                <th className="pb-2 text-right font-medium">원가 (₩/1k)</th>
                <th className="pb-2 text-right font-medium">판매 (크레딧/1k)</th>
              </tr>
            </thead>
            <tbody>
              {MODEL_RATES.map((m) => (
                <tr key={m.model} className="border-t border-ink-100">
                  <td className="py-1.5">{m.tier}</td>
                  <td className="py-1.5">{m.model}</td>
                  <td className="py-1.5 text-right tabular-nums">{m.usd}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-500">{(m.inNc / 4).toFixed(1)} / {(m.outNc / 4).toFixed(1)}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{m.inNc} / {m.outNc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[12px] text-ink-400">
          입력/출력 순 · 판매 = 원가 × 4.0. Sonnet 5는 8/31까지 인트로가($2/$10)지만 정가 기준으로 청구 — 그 기간 마진이 오히려 유리.
        </p>
      </Panel>

      {/* ③ 크레딧 정책 + 이미지 정액 */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="크레딧 정책">
          <div className="divide-y divide-ink-100">
            {CREDIT_POLICY.map((c) => (
              <div key={c.k} className="flex items-baseline justify-between gap-3 py-2 text-[13px]">
                <span className="shrink-0 text-ink-500">{c.k}</span>
                <span className="text-right">
                  <b className="tabular-nums text-ink-800">{c.v}</b>
                  <span className="block text-[12px] text-ink-400">{c.note}</span>
                </span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="이미지 생성 정액표"
               action={<InfoTooltip text="유일한 정액 과금 도구. 1단 스펙 호출 원가는 imagegen_spec 으로 별도 기록 중 — 데이터가 쌓이면 정액 재산정(도구별 배수표에서 감시)." />}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">품질</th>
                <th className="pb-2 text-right font-medium">원가</th>
                <th className="pb-2 text-right font-medium">판매</th>
                <th className="pb-2 text-right font-medium">근거</th>
              </tr>
            </thead>
            <tbody>
              {IMAGE_FLAT_ROWS.map((r) => (
                <tr key={r.q} className="border-t border-ink-100">
                  <td className="py-1.5">{r.q}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-500">{fmt.krw(r.cost)}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{fmt.nc(r.sell)}크레딧</td>
                  <td className="py-1.5 text-right text-[12px] text-ink-400">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      {/* ④ 감시 임계값 — 계기판 경고등의 규칙서 */}
      <Panel title="감시 임계값 (계기판 경고등의 판정 규칙)">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">지표</th>
                <th className="pb-2 font-medium">임계</th>
                <th className="pb-2 font-medium">의미</th>
              </tr>
            </thead>
            <tbody>
              {THRESHOLDS.map((t2) => (
                <tr key={t2.name} className="border-t border-ink-100">
                  <td className="py-1.5 font-medium text-ink-700">{t2.name}</td>
                  <td className="py-1.5 tabular-nums text-amber-700">{t2.limit}</td>
                  <td className="py-1.5 text-ink-500">{t2.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ⑤ 정책 변경 이력 — 거버넌스의 기억 */}
      <Panel title="정책 변경 이력">
        <ol className="relative ml-2 space-y-3 border-l border-ink-200 pl-4">
          {POLICY_HISTORY.map((h) => (
            <li key={h.date} className="relative text-[13px]">
              <span className={`absolute -left-[21.5px] top-1 h-2.5 w-2.5 rounded-full ${h.future ? "border border-ink-300 bg-white" : "bg-brand-500"}`} />
              <span className={`tabular-nums ${h.future ? "text-ink-400" : "text-ink-500"}`}>{h.date}</span>
              <b className={`ml-2 ${h.future ? "text-ink-500" : "text-ink-800"}`}>{h.what}</b>
              <span className="ml-2 text-ink-400">— {h.why}</span>
            </li>
          ))}
        </ol>
      </Panel>

      {/* ⑥ 사람이 할 일 */}
      <Panel title="월간 대사 체크리스트 (매월 1일, ~30분)"
             action={<span className="text-[12px] text-ink-400">상세 절차: docs/team/FINOPS-RECONCILIATION.md · 단가 원본: docs/reference/claude-bedrock-pricing.md</span>}>
        <ol className="list-decimal space-y-1.5 pl-5 text-[13px] text-ink-600">
          {CHECKLIST.map((c, i) => <li key={i}>{c}</li>)}
        </ol>
      </Panel>
    </>
  );
}

export default RulesSegment;
